/**
 * V2 Paywall Header Section Configuration
 *
 * V2 version of paywallHeaderSectionConfig.ts with V2 data paths.
 * Close button and restore label live in content.paywall.* (NOT in shared header).
 *
 * V2 path mapping:
 * - V1 navigationBar.close → V2 content.paywall.closeButton
 * - V1 navigationBar.restore → V2 content.paywall.restoreLabel
 * - V1 navigationBar.styles → V2 content.paywall.headerAppearance
 *
 * Enum mapping:
 * - V1 'left'/'right' → V2 'start'/'end' (HorizontalEdge)
 * - V1 'visibleAfterTimer' → V2 'delayedVisible' (CloseVisibility)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import { createTextStyleFields, V2_TITLE_TEXT_STYLE_FIELDS } from './atomicFields/textStyleFields';
import { V2_TEXT_ALIGN_OPTIONS } from './atomicFields/options';

// ============================================
// Close Button Configs (V2 paths)
// ============================================

export const V2_PAYWALL_CLOSE_BUTTON_CONFIG: FieldConfig = {
  path: 'content.paywall.closeButton',
  label: 'Close Button',
  type: 'toggle',
  defaultValue: null,
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-header-close-button',
};

export const V2_PAYWALL_CLOSE_ICON_COLOR_CONFIG: FieldConfig = {
  path: 'content.paywall.closeButton.appearance.iconColor',
  label: 'Icon Color',
  type: 'color',
  defaultValue: '#ffffff',
  isNullable: false,
  guideKey: 'screenedit-paywall-header-close-icon-color',
};

/**
 * V2 uses HorizontalEdge enum: 'start'/'end' instead of V1 'left'/'right'
 */
export const V2_PAYWALL_CLOSE_ALIGNMENT_CONFIG: FieldConfig = {
  path: 'content.paywall.headerAppearance.closeAlignment',
  label: 'Alignment',
  type: 'select',
  options: [
    { value: 'start', label: 'Left' },
    { value: 'end', label: 'Right' },
  ],
  defaultValue: 'start',
  isNullable: true,
  guideKey: 'screenedit-paywall-header-close-alignment',
};

/**
 * V2 uses CloseVisibility enum: 'delayedVisible' instead of V1 'visibleAfterTimer'
 */
export const V2_PAYWALL_CLOSE_APPEARANCE_CONFIG: FieldConfig = {
  path: 'content.paywall.headerAppearance.closeVisibility',
  label: 'Appearance',
  type: 'select',
  options: [
    { value: 'visible', label: 'Visible' },
    { value: 'delayedVisible', label: 'Visible after timer' },
  ],
  defaultValue: 'visible',
  isNullable: true,
  guideKey: 'screenedit-paywall-header-close-visibility',
};

export const V2_PAYWALL_CLOSE_TIMER_CONFIG: FieldConfig = {
  path: 'content.paywall.headerAppearance.closeDelaySeconds',
  label: 'Visible after',
  type: 'slider',
  min: 1,
  max: 30,
  step: 1,
  defaultValue: 5,
  isNullable: true,
  guideKey: 'screenedit-paywall-header-close-delay',
};

export const V2_PAYWALL_CLOSE_STYLE_FIELDS: FieldConfig[] = [
  V2_PAYWALL_CLOSE_ICON_COLOR_CONFIG,
  V2_PAYWALL_CLOSE_ALIGNMENT_CONFIG,
  V2_PAYWALL_CLOSE_APPEARANCE_CONFIG,
  V2_PAYWALL_CLOSE_TIMER_CONFIG,
];

// ============================================
// Restore Button Configs (V2 paths)
// ============================================

export const V2_PAYWALL_RESTORE_BUTTON_CONFIG: FieldConfig = {
  path: 'content.paywall.restoreLabel',
  label: 'Restore Button',
  type: 'toggle',
  defaultValue: null,
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-header-restore-button',
};

/**
 * Remap V1 text style fields to V2 TypographyStyle:
 *   .styles.* → .appearance.*
 *   .appearance.textAlign → .appearance.align
 *   textAlign options: left/right → start/end
 */
function remapTextFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    const isAlignField = path.endsWith('.appearance.textAlign');
    if (isAlignField) {
      path = path.replace('.appearance.textAlign', '.appearance.align');
      return { ...f, path, options: [...V2_TEXT_ALIGN_OPTIONS] };
    }
    return path !== f.path ? { ...f, path } : f;
  });
}

/**
 * V2 uses 'appearance' instead of V1 'styles', and 'align' instead of 'textAlign'
 */
export const V2_PAYWALL_RESTORE_STYLE_FIELDS: FieldConfig[] = remapTextFieldsForV2(
  createTextStyleFields('content.paywall.restoreLabel', V2_TITLE_TEXT_STYLE_FIELDS)
);

// ============================================
// Factory Functions (V2 structures)
// ============================================

/**
 * Create default V2 close button (TapTarget)
 */
export function createDefaultV2CloseButton(iconColor: string = '#ffffff'): Record<string, unknown> {
  return {
    kind: 'icon',
    iconName: 'xmark',
    appearance: {
      iconColor,
    },
    layout: {
      insets: {},
    },
    action: {
      kind: 'advance',
      edges: [],
    },
  };
}

/**
 * Create default V2 restore label (LocalizedLabel)
 */
export function createDefaultV2RestoreLabel(locale: string): Record<string, unknown> {
  return {
    translations: { [locale]: 'Restore Purchases' },
    kind: 'static',
    parameters: { labels: {}, links: {} },
    appearance: {
      fontSize: 14,
      fontFamily: 'SF Pro',
      fontWeight: 400,
      color: '#666666',
      lineHeight: 20,
      align: 'center',
    },
    layout: { insets: {} },
  };
}

/**
 * Create default V2 header appearance (PaywallHeaderStyle)
 */
export function createDefaultV2HeaderAppearance(): Record<string, unknown> {
  return {
    closeAlignment: 'start',
    closeVisibility: 'visible',
    closeDelaySeconds: 5,
  };
}
